import { Component, Input, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core'
import { Router, NavigationEnd } from '@angular/router'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

export interface TabConfig {
  label: string
  route: string
  icon?: string
  loading?: boolean
}

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabsComponent implements OnDestroy {
  @Input() tabs: TabConfig[] = []
  @Input() baseRoute: string = ''
  @Input() activeTabIndex: number = 0
  @Input() useRouting: boolean = true
  @Input() scrollTargetId?: string
  @Output() tabChange = new EventEmitter<number>()

  private destroy$ = new Subject<void>()

  constructor(private router: Router) {
    // Listen for navigation end events to scroll to content
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        if (this.useRouting && this.scrollTargetId) {
          // Use setTimeout to ensure DOM is updated after route change
          setTimeout(() => {
            const element = document.getElementById(this.scrollTargetId)
            if (element) {
              // Calculate offset for sticky header and tabs
              const headerElement = document.querySelector('.header-container')
              const tabsElement = document.querySelector('app-tabs .tabs-container')

              let offset = 0
              if (headerElement) {
                offset += headerElement.getBoundingClientRect().height
              }
              if (tabsElement) {
                offset += tabsElement.getBoundingClientRect().height
              }

              // Get element position and scroll with offset
              const elementPosition = element.getBoundingClientRect().top + window.pageYOffset
              window.scrollTo({
                top: elementPosition - offset,
                behavior: 'smooth',
              })
            }
          }, 0)
        }
      })
  }

  isActive(tab: TabConfig): boolean {
    if (!this.useRouting) {
      const tabIndex = this.tabs.indexOf(tab)
      return tabIndex === this.activeTabIndex
    }

    // Strip query parameters from URL for comparison
    const currentUrl = this.router.url.split('?')[0]
    const fullRoute = this.getFullRoute(tab)

    // Handle empty route as the default/index tab
    if (tab.route === '') {
      // Active if we're at the base route exactly
      return currentUrl === this.baseRoute || currentUrl === `${this.baseRoute}/`
    }

    // Active if current URL starts with the full route
    return currentUrl.startsWith(fullRoute)
  }

  navigateToTab(tab: TabConfig): void {
    if (!this.useRouting) {
      const tabIndex = this.tabs.indexOf(tab)
      this.tabChange.emit(tabIndex)
      return
    }

    const fullRoute = this.getFullRoute(tab)
    this.router.navigateByUrl(fullRoute)
  }

  private getFullRoute(tab: TabConfig): string {
    if (tab.route === '') {
      return this.baseRoute
    }
    return `${this.baseRoute}/${tab.route}`
  }

  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
